import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ApiClientError,
  extractLab,
  type ApiClientErrorCode,
  type LabColor,
  type PaintMatchRequest,
  type PaintMatchResponseItem,
  type Region,
} from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { downscaleImageToImageData } from '@/components/PhotoTools/imageDownscale';
import { PhotoPickerView, type PhotoPickerState } from './PhotoPickerView';

/**
 * Container PaintPhotoPicker (PRD §6 Block 3 Phase 4 Шаг 5).
 * См. ADR-0005, ADR-0008, ADR-0009, ADR-0010 §5.
 *
 * **Второй legitimate consumer of `extractLab`** в production-коде —
 * см. ESLint allowlist в `eslint.config.js` (расширен Шагом 0).
 *
 * P-009 4-уровневая защита для paint:
 *   (1) `extractLab(ImageData, Region)` принимает только `ImageData`.
 *   (2) `PaintMatchRequestSchema.strict()` блокирует extra поля (no `kind`,
 *       no `photoBlob`).
 *   (3) ESLint allowlist изолирует import этого файла (ADR-0010 §5).
 *   (4) MSW request inspection в `PhotoPicker.test.tsx` — body shape =
 *       {mode, l, a, b} ровно 4 ключа, никакого kind/photo/blob.
 *   (5) E2E network snapshot — Phase 5 (`paint-p009-network-check.spec.ts`).
 *
 * Pipeline (симметрия с decor PhotoPicker):
 *   File → URL.createObjectURL → <img>.decode() → downscaleImageToImageData
 *   (≤1024×1024 для OOM safety) → extractLab(ImageData, Region) → POST с
 *   {mode, l, a, b}. Фото не уезжает — только числа.
 *
 * Differences vs decor:
 *   - URL: `/paint/photo` (без kind, paint один домен).
 *   - Request: `{mode, l, a, b}` (no `kind` per ADR-0010 §1).
 *   - Navigate after click: `/paint/:globalId` (no kind segment).
 */
const DEFAULT_RADIUS_PERCENT = 10;

export function PhotoPicker() {
  const navigate = useNavigate();

  const [state, setState] = useState<PhotoPickerState>('noImage');
  const [imageEl, setImageEl] = useState<HTMLImageElement | null>(null);
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [cx, setCx] = useState(0);
  const [cy, setCy] = useState(0);
  const [radiusPercent, setRadiusPercent] = useState(DEFAULT_RADIUS_PERCENT);
  const [matchItems, setMatchItems] = useState<readonly PaintMatchResponseItem[] | undefined>(
    undefined,
  );
  const [error, setError] = useState<{ code: ApiClientErrorCode; message: string } | undefined>(
    undefined,
  );

  // Все ObjectURL'ы которые мы создали. Без этого set'а URL созданный в
  // onFileSelect до setImageUrl() (т.е. unmount во время `await img.decode()`)
  // утекает: cleanup-effect видит только `imageUrl` из state, а не in-flight `url`.
  const createdUrlsRef = useRef<Set<string>>(new Set());
  // Каждый вызов onFileSelect получает уникальный sessionId. Если пока
  // decode шёл, юзер выбрал другой файл — старый sessionId становится
  // stale и его result отбрасывается (race на быстром re-selection).
  const uploadIdRef = useRef(0);

  const minSide = imageWidth > 0 && imageHeight > 0 ? Math.min(imageWidth, imageHeight) : 0;
  const radius = Math.max(1, Math.round((minSide * radiusPercent) / 100));

  useEffect(() => {
    const created = createdUrlsRef.current;
    return () => {
      for (const u of created) URL.revokeObjectURL(u);
      created.clear();
    };
  }, []);

  function revokeAndUntrack(url: string) {
    URL.revokeObjectURL(url);
    createdUrlsRef.current.delete(url);
  }

  async function onFileSelect(file: File) {
    if (imageUrl) revokeAndUntrack(imageUrl);
    setError(undefined);
    const sessionId = ++uploadIdRef.current;
    const url = URL.createObjectURL(file);
    createdUrlsRef.current.add(url);
    const img = new Image();
    img.src = url;
    try {
      await img.decode();
      if (sessionId !== uploadIdRef.current) {
        revokeAndUntrack(url);
        return;
      }
      setImageEl(img);
      setImageUrl(url);
      setImageWidth(img.naturalWidth);
      setImageHeight(img.naturalHeight);
      setCx(Math.round(img.naturalWidth / 2));
      setCy(Math.round(img.naturalHeight / 2));
      setRadiusPercent(DEFAULT_RADIUS_PERCENT);
      setState('imageLoaded');
    } catch {
      revokeAndUntrack(url);
      if (sessionId !== uploadIdRef.current) return;
      setError({ code: 'INTERNAL_ERROR', message: 'Не удалось прочитать фото' });
      setState('error');
    }
  }

  async function runMatch(mode: 'area' | 'average') {
    if (!imageEl) return;
    setState('loading');
    setError(undefined);
    try {
      const { imageData, scale } = downscaleImageToImageData(imageEl);
      const region: Region =
        mode === 'area'
          ? { type: 'circle', cx: cx * scale, cy: cy * scale, r: radius * scale }
          : { type: 'average' };
      const lab: LabColor = extractLab(imageData, region);
      // P-009: только {mode, l, a, b}. NO kind (paint один домен — ADR-0010 §1).
      // PaintMatchRequestSchema.strict() добавляет runtime-страховку.
      const request: PaintMatchRequest = {
        mode,
        l: lab.l,
        a: lab.a,
        b: lab.b,
      };
      const response = await apiClient.paint.match(request);
      setMatchItems(response.items);
      setState('results');
    } catch (e: unknown) {
      const apiErr =
        e instanceof ApiClientError
          ? { code: e.code, message: e.message }
          : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
      setError(apiErr);
      setState('error');
    }
  }

  function onResetImage() {
    if (imageUrl) revokeAndUntrack(imageUrl);
    ++uploadIdRef.current;
    setState('noImage');
    setImageEl(null);
    setImageUrl(undefined);
    setImageWidth(0);
    setImageHeight(0);
    setMatchItems(undefined);
    setError(undefined);
  }

  return (
    <PhotoPickerView
      state={state}
      imageUrl={imageUrl}
      imageWidth={imageWidth}
      imageHeight={imageHeight}
      cx={cx}
      cy={cy}
      radius={radius}
      radiusPercent={radiusPercent}
      matchItems={matchItems}
      error={error}
      onFileSelect={onFileSelect}
      onAverageMatch={() => runMatch('average')}
      onActivatePicker={() => setState('pickerActive')}
      onAreaMatch={() => runMatch('area')}
      onBackToImageLoaded={() => setState('imageLoaded')}
      onCircleMove={(newCx, newCy) => {
        setCx(newCx);
        setCy(newCy);
      }}
      onRadiusChange={setRadiusPercent}
      onResetImage={onResetImage}
      onItemClick={(globalId) => navigate(`/paint/${encodeURIComponent(globalId)}`)}
    />
  );
}
